import { useEffect, useRef, useState } from "react";

import { addressDetails, autocompleteAddress, type AddressDetails, type AddressSuggestion } from "../../api/postcodeLookup";
import type { Vertaal } from "./bouwstenen";

/**
 * Eén adresveld met de suggesties van Postcode.eu, zoals het relatiescherm ze geeft: typ straat, plaats
 * of postcode en kies een suggestie. Een suggestie die nog geen adres is (een plaats, een straat, een
 * postcode) gaat een stap dieper: daarna volgen zijn huisnummers.
 *
 * <p>Postcode.eu rekent per zoeksessie, niet per toetsaanslag: één sessie-id per veld, en de aanroep
 * wacht 300 ms. Zonder sleutels op de server zegt het veld dat, in plaats van te doen alsof er geen
 * adres bestaat.
 */
export function AdresZoeker({
  id,
  waarde,
  onWaarde,
  onAdres,
  plaatshouder,
  t,
}: {
  id?: string;
  waarde: string;
  onWaarde: (waarde: string) => void;
  /** Het gekozen adres, uit elkaar gehaald; de leesbare regel staat dan in het veld. */
  onAdres?: (adres: AddressDetails) => void;
  plaatshouder?: string;
  t: Vertaal;
}) {
  const [suggesties, setSuggesties] = useState<AddressSuggestion[]>([]);
  /** Bij welke term de suggesties horen: anders blijft de lijst van een vorige toetsaanslag staan. */
  const [voorTerm, setVoorTerm] = useState("");
  const [nietBeschikbaar, setNietBeschikbaar] = useState(false);
  const [gekozen, setGekozen] = useState<string | null>(null);
  const [context, setContext] = useState("nld");
  const sessie = useRef(`fawkes-${Math.random().toString(36).slice(2)}-${Date.now()}`).current;
  const volgnummer = useRef(0);
  const lijst = useRef<HTMLUListElement>(null);
  const toont = gekozen == null && suggesties.length > 0 && voorTerm === waarde.trim();

  // Onderaan een venster valt de lijst anders buiten beeld, achter de knoppenbalk.
  useEffect(() => {
    if (toont) lijst.current?.scrollIntoView?.({ block: "nearest" });
  }, [toont, suggesties]);

  useEffect(() => {
    const term = waarde.trim();
    // Een volledige term ("7581WL 7") zoekt altijd op landniveau; de context is alleen voor het korte
    // vervolg vlak na het induiken, als je een huisnummer in een gekozen straat of postcode typt.
    const diep = context !== "nld";
    const metContext = diep && term.length < 3;
    const ctx = metContext ? context : "nld";
    if (gekozen != null || term.length < (metContext ? 0 : 3)) {
      setSuggesties([]);
      return;
    }
    const nr = ++volgnummer.current;
    const timer = window.setTimeout(() => {
      void autocompleteAddress(term, sessie, ctx).then((res) => {
        if (nr !== volgnummer.current) return;
        setNietBeschikbaar(res.unavailable);
        setSuggesties(res.matches);
        setVoorTerm(term);
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [waarde, gekozen, context, sessie]);

  const kies = async (s: AddressSuggestion) => {
    if (s.precision && s.precision !== "Address") {
      setContext(s.context);
      setSuggesties([]);
      setGekozen(null);
      onWaarde("");
      return;
    }
    const adres = await addressDetails(s.context, sessie);
    setSuggesties([]);
    if (!adres) {
      setContext(s.context);
      onWaarde("");
      return;
    }
    const regel = adresRegel(adres) || s.label;
    setGekozen(regel);
    onWaarde(regel);
    onAdres?.(adres);
  };

  return (
    <div className="nr-adres">
      <input
        id={id}
        className="nr-invoer"
        type="text"
        autoComplete="off"
        placeholder={plaatshouder}
        value={waarde}
        onChange={(e) => {
          onWaarde(e.target.value);
          setGekozen(null);
          // Een leeg veld begint weer op landniveau; anders blijf je in de laatst gekozen straat hangen.
          if (!e.target.value.trim()) setContext("nld");
        }}
      />
      {toont && (
        <ul ref={lijst} className="nr-suggesties" aria-label={t("nieuweReservering.adres.suggesties")}>
          {suggesties.map((s) => (
            <li key={s.context}>
              <button type="button" onClick={() => void kies(s)}>
                <strong>{s.label}</strong>
                {s.description && <span className="nr-klein">{s.description}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {nietBeschikbaar && (
        <span className="nr-klein" role="status">
          {t("nieuweReservering.adres.nietBeschikbaar")}
        </span>
      )}
    </div>
  );
}

/** "Hogenhof 7, 7581 WL Losser". */
export function adresRegel(a: AddressDetails): string {
  return [
    [a.street, a.buildingNumber, a.buildingNumberAddition].filter(Boolean).join(" "),
    [a.postcode, a.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
}
